import { z } from 'zod';
import type { UserMemory } from './memory.ts';

/** Setup after call 1 (Screen 2): resume and interview details, plus the (hidden) reminder preferences. Written as source 'form'. */

export const RESUME_MAX_BYTES = 5_000_000;

// What the text LLM extracts from a resume PDF (the PDF itself is never stored). Lenient: a wordy reply is trimmed
// to fit and a numeric string is read as a number, rather than failing the whole upload.
const list = (items: number, chars: number) =>
  z
    .array(z.string())
    .nullish()
    .transform((a) => (a ?? []).slice(0, items).map((s) => s.slice(0, chars)));
export const resumeExtract = z.object({
  summary: z
    .string()
    .min(1)
    .transform((s) => s.slice(0, 600)),
  roles: list(20, 100),
  years: z.coerce
    .number()
    .min(0)
    .max(60)
    .nullish()
    .catch(null)
    .transform((y) => y ?? null),
  skills: list(40, 60),
  projects: list(20, 200),
});
export type ResumeExtract = z.infer<typeof resumeExtract>;

// Empty strings clear a field the user emptied; `jd` absent leaves the job description untouched.
export const interviewForm = z.object({
  role: z.string().max(100),
  company: z.string().max(100),
  date: z.string().max(40),
  round: z.string().max(100),
  jd: z.string().max(20_000).optional(),
});
export type InterviewForm = z.infer<typeof interviewForm>;

export const NUDGE_CHANNELS = ['whatsapp', 'push', 'none'] as const;

/** Digits with an optional leading +, 10 to 15 digits (spaces and dashes are ignored). */
export function normalizePhone(raw: string): string | null {
  const compact = raw.replace(/[\s-]/g, '');
  return /^\+?\d{10,15}$/.test(compact) ? compact : null;
}

export const nudgeForm = z
  .object({
    channel: z.enum(NUDGE_CHANNELS),
    phone: z.string().max(20).optional(),
    whatsappConsent: z.boolean(),
    bestTime: z.string().max(60),
    timezone: z.string().max(60).optional(),
  })
  .superRefine((f, ctx) => {
    if (f.channel !== 'whatsapp') return;
    if (!f.phone || !normalizePhone(f.phone))
      ctx.addIssue({ code: 'custom', path: ['phone'], message: 'phone number' });
    if (!f.whatsappConsent) ctx.addIssue({ code: 'custom', path: ['whatsappConsent'], message: 'consent required' });
  });

export type SetupResponse = { memory: UserMemory } | { error: string };
